import { Component } from 'react'
import { fetchLoanOverrides } from '../../store/reducers/loanOverrides/loanOverridesSlice'

/**
 * Base component with shared methods for order creation modals
 * This component is meant to be extended by ModalCreateOrderContainer and ChainedCreateOrderModal
 */
class BaseOrderModalContainer extends Component {
  // Debug counter for tracking override calls
  static overrideCallCounter = 0
  /**
   * Get fee management overrides from the backend
   * Fetches overrides for product fees, due dates, etc.
   *
   * @param {Array} productIds - List of product IDs
   * @param {Array} dataTypes - List of data types to fetch
   * @param {string} panelistId - Optional panelist ID for appraiser-specific overrides
   * @param {string} priority - Priority for the override lookup
   * @return {Object} - The overrides result
   */
  getFeeMgmtOverrides = async (productIds, dataTypes, panelistId = null) => {
    const { selectedLoan, dispatch } = this.props

    if (!productIds || !productIds.length) {
      return {}
    }

    try {
      const params = {
        loanId:
          selectedLoan.id ||
          (this.state?.currentLoanIndex !== undefined ? this.props.loans[this.state.currentLoanIndex].id : null),
        productIds: productIds,
        dataTypes: dataTypes,
        panelistId: panelistId,
      }
      // Dispatch the action to fetch loan overrides
      const result = await dispatch(fetchLoanOverrides(params))

      return result.payload.results || {}
    } catch (error) {
      return {}
    }
  }

  getValueFromOverride = (override, dataType) => {
    if (!override) {
      return null
    }

    // Handle new response structure where override data is directly on the override object
    if (override.override_data && override.override_data[dataType]) {
      return override.override_data[dataType]
    }

    // Handle updated response structure with direct fields
    if (dataType === 'fee') {
      return override.geoOverrideAmount
    } else if (dataType === 'due_date') {
      return override.geoOverrideDueDate
    }

    return null
  }

  /**
   * Gets product override amounts handling both new and legacy methods
   * @param {Array} productIds - Product IDs to check
   * @param {function} legacyCb - Legacy overrides callback function
   * @param {boolean} returnDueDate - Whether to return due date overrides
   * @param {string} panelistId - Optional panelist ID for appraiser-specific overrides
   * @param {string} priority - Priority for the override lookup
   * @returns {Promise<Object>} Map of product IDs to their override amounts
   */
  getProductOverrides = async (productIds, legacyCb, returnDueDate = false, panelistId = null) => {
    const dataTypes = returnDueDate ? 'DUE_DATE' : 'FEE'

    if (!productIds || !productIds.length) {
      return {}
    }

    if (this.shouldShowOverrideSourceTracking()) {
      const overrides = await this.getFeeMgmtOverrides(productIds, [dataTypes], panelistId)

      // Store full override details for source tracking
      if (overrides && Object.keys(overrides).length > 0) {
        this.setState((prevState) => {
          const updatedDetails = { ...prevState.productOverrideDetails }

          // If this is appraiser-specific, store with compound key
          if (panelistId) {
            Object.keys(overrides).forEach((productId) => {
              const key = `${productId}_${panelistId}`
              updatedDetails[key] = overrides[productId]
            })
          } else {
            // General product overrides
            Object.keys(overrides).forEach((productId) => {
              updatedDetails[productId] = overrides[productId]
            })
          }

          return {
            productOverrideDetails: updatedDetails,
          }
        })
      }

      const result = Object.keys(overrides).reduce((acc, productId) => {
        const overrideValue = this.getValueFromOverride(overrides[productId], dataTypes.toLowerCase())
        if (overrideValue !== null && overrideValue !== undefined) {
          acc[productId] = overrideValue
        }
        return acc
      }, {})
      return result
    }

    // Legacy method
    const legacyOverrides = await legacyCb()
    const result = Object.keys(legacyOverrides).reduce((acc, productId) => {
      const overrideValue = returnDueDate
        ? legacyOverrides[productId].geoOverrideDueDate
        : legacyOverrides[productId].geoOverrideAmount
      if (overrideValue !== null && overrideValue !== undefined) {
        acc[productId] = overrideValue
      }
      return acc
    }, {})
    return result
  }

  /**
   * Fetch appraiser-specific fee overrides for a given appraiser
   * @param {string} appraiserId - The appraiser ID (panelist_id)
   * @returns {Promise<Object>} Override data for the appraiser
   */
  fetchAppraiserOverrides = async (appraiserId) => {
    const { selected_products } = this.state
    const product_ids = selected_products
      .filter((product) => product && !product.manual_fee_override_exists)
      .map((product) => product.id)

    if (!product_ids.length) {
      return {}
    }

    const state = this.getPropertyState()
    const zip = this.getPropertyZip()

    const result = await this.getProductOverrides(
      product_ids,
      () => this.getPricesFromProducts(product_ids, state, zip, false),
      false,
      appraiserId,
    )

    return result
  }

  /**
   * Calculate additional fees based on priority and integration rush fees
   * @param {Array} currentAdditionalFees - Current additional fees array
   * @param {Object} overrideData - Override data from backend
   * @param {string} priority - Current priority ('Rush' or 'Normal')
   * @param {number} currentKeyCounter - Current key counter value
   * @param {Array} selectedProducts - Currently selected products for fallback rush fees
   * @returns {Object} { additional_fees, hasAdditionalFees, keyCounter }
   */
  calculateAdditionalFees = (
    currentAdditionalFees,
    overrideData,
    priority,
    currentKeyCounter,
    selectedProducts = [],
    useDefaultRushFee = false,
    defaultRushFee = null,
  ) => {
    // Early return for AMC lenders
    if (this.props.is_amc_lender) {
      return {
        additional_fees: currentAdditionalFees,
        hasAdditionalFees: currentAdditionalFees.length > 0,
        keyCounter: currentKeyCounter,
      }
    }

    // Helper function to extract base product ID
    const getBaseProductId = (productId) => productId?.split('_')[0] || productId

    // Helper function to check if fee is rush fee
    const isRushFee = (fee) => fee.isIntegrationRushFee || fee.isProductRushFee || fee.isDefaultRushFee

    // Calculate max key counter once
    let keyCounter =
      currentAdditionalFees.length > 0
        ? Math.max(currentKeyCounter, ...currentAdditionalFees.map((fee) => fee.key || 0))
        : currentKeyCounter

    // Get selected product IDs (cached for performance)
    const selectedProductIds = new Set(selectedProducts.filter((p) => p).map((p) => p.id))

    let updatedAdditionalFees = [...currentAdditionalFees]

    // Create improved rush fee detection function (reused for both Rush and Normal priority)
    const isRushFeeByProduct = (fee) => {
      // Traditional flag-based detection (works for new fees)
      if (isRushFee(fee)) { return true }

      // Product-based detection (works for backend fees)
      if (fee.productId) {
        const product = selectedProducts.find(p => p && p.id === fee.productId)
        return product && product.using_default_rush_fee
      }

      // Global/Integration rush fee detection
      return fee.description === 'Rush Fee' || fee.description === 'Integration Rush Fee'
    }

    if (priority === 'Rush') {
      // Filter out rush fees for unselected products in one pass
      updatedAdditionalFees = updatedAdditionalFees.filter((fee) => {
        if (!isRushFeeByProduct(fee)) {
          return true
        }
        const baseProductId = getBaseProductId(fee.productId)
        return selectedProductIds.has(baseProductId)
      })

      // Build set of products with existing rush fees using our improved detection
      const productsWithExistingRushFees = new Set(
        updatedAdditionalFees
          .filter(isRushFeeByProduct)
          .map((fee) => getBaseProductId(fee.productId))
          .filter(Boolean),
      )

      // Add integration rush fees
      Object.entries(overrideData).forEach(([productKey, overrideItem]) => {
        const { integrationRushFee } = overrideItem
        if (integrationRushFee != null) {
          const baseProductId = getBaseProductId(productKey)
          if (!productsWithExistingRushFees.has(baseProductId)) {
            updatedAdditionalFees.push({
              amount: integrationRushFee,
              description: 'Integration Rush Fee',
              key: ++keyCounter,
              productId: productKey,
              isIntegrationRushFee: true,
            })
            productsWithExistingRushFees.add(baseProductId)
          }
        }
      })

      // Add product default rush fees
      const productsNeedingRushFees = selectedProducts.filter(
        (product) =>
          product &&
          !productsWithExistingRushFees.has(product.id) &&
          product.using_default_rush_fee &&
          product.default_rush_fee,
      )

      productsNeedingRushFees.forEach((product) => {
        updatedAdditionalFees.push({
          amount: product.default_rush_fee,
          description: 'Product Rush Fee',
          key: ++keyCounter,
          productId: product.id,
          isProductRushFee: true,
        })
        productsWithExistingRushFees.add(product.id)
      })

      // If none of the products have rush fees, apply the global default rush fee as fallback
      const hasAnyRushFees = updatedAdditionalFees.some(isRushFeeByProduct)
      if (!hasAnyRushFees && useDefaultRushFee && defaultRushFee) {
        updatedAdditionalFees.push({
          amount: defaultRushFee,
          description: 'Rush Fee',
          key: ++keyCounter,
          isDefaultRushFee: true,
        })
      }
    } else {
      // Remove all rush fees for Normal priority
      updatedAdditionalFees = updatedAdditionalFees.filter((fee) => !isRushFeeByProduct(fee))
    }

    // Remove placeholder fees if we have real fees
    const realFees = updatedAdditionalFees.filter((fee) => fee.description !== null)
    if (realFees.length > 0) {
      updatedAdditionalFees = realFees
    }

    // Handle empty fees case - only add placeholder if we have no real fees
    const realFeesExist = updatedAdditionalFees.some((fee) => fee.description !== null)
    const hasAdditionalFees = realFeesExist

    if (!realFeesExist) {
      updatedAdditionalFees = [this.getInitialAdditionalFee()]
    }

    return {
      additional_fees: updatedAdditionalFees,
      hasAdditionalFees,
      keyCounter,
    }
  }

  /**
   * Groups override data by base product ID
   * @param {Object} overrideData - Override data from backend
   * @returns {Object} Grouped overrides by product ID
   */
  groupOverridesByProduct = (overrideData) => {
    return Object.entries(overrideData).reduce((groups, [productKey, data]) => {
      const baseProductId = productKey.split('_')[0]

      if (!groups[baseProductId]) {
        groups[baseProductId] = []
      }

      groups[baseProductId].push({ key: productKey, data })
      return groups
    }, {})
  }

  /**
   * Sorts overrides by priority: appraiser-specific first, then actual overrides, then base amounts
   * @param {Array} overrides - Array of override objects
   * @returns {Array} Sorted overrides
   */
  sortOverridesByPriority = (overrides) => {
    return overrides.sort((a, b) => {
      const aIsAppraiserSpecific = a.key.includes('_')
      const bIsAppraiserSpecific = b.key.includes('_')

      // First priority: appraiser-specific overrides
      if (aIsAppraiserSpecific !== bIsAppraiserSpecific) {
        return bIsAppraiserSpecific - aIsAppraiserSpecific // true (1) comes before false (0)
      }

      // Second priority: actual overrides (any boolean true except base_product_amount)
      const aHasActualOverride = Object.entries(a.data.overrideSourceTracking || {}).some(
        ([key, value]) =>
          key !== 'base_product_amount' &&
          key !== 'fee_mgmt_override_sheet_name' &&
          key !== 'integration_fee_error_msg' &&
          value === true,
      )
      const bHasActualOverride = Object.entries(b.data.overrideSourceTracking || {}).some(
        ([key, value]) =>
          key !== 'base_product_amount' &&
          key !== 'fee_mgmt_override_sheet_name' &&
          key !== 'integration_fee_error_msg' &&
          value === true,
      )

      return bHasActualOverride - aHasActualOverride // true (1) comes before false (0)
    })
  }

  /**
   * Calculates final amount for a product given its override and full product data
   * @param {Object} selectedOverride - The highest priority override
   * @param {Object} selectedProduct - The selected product
   * @param {Object} fullProduct - The full product data
   * @returns {number} Final calculated amount
   */
  calculateFinalProductAmount = (selectedOverride, selectedProduct, fullProduct) => {
    const overrideAmount = selectedOverride.data.geoOverrideAmount || selectedOverride.data
    const hasIntegrationError = selectedOverride.data.overrideSourceTracking?.integration_fee_error_msg?.trim()

    if (hasIntegrationError) {
      // If there's an integration error, use the original product amount
      const originalAmount =
        fullProduct?.original_amount !== undefined ? fullProduct.original_amount : selectedProduct.amount
      return parseFloat(originalAmount)
    } else {
      // Normal override logic - determine final amount based on comparison with full product list
      if (
        fullProduct?.original_amount &&
        (fullProduct.original_amount === overrideAmount ||
          fullProduct.amount === overrideAmount ||
          fullProduct.amount !== fullProduct.original_amount)
      ) {
        return parseFloat(fullProduct.amount)
      } else {
        return parseFloat(overrideAmount)
      }
    }
  }

  /**
   * Applies all overrides to products and returns updated product amounts map
   * @param {Object} overridesByProduct - Grouped overrides by product
   * @param {Array} validProducts - Valid (non-null) selected products
   * @param {Map} fullProductsMap - Map of full product data
   * @param {Object} manualOverrideTracking - Manual override tracking object
   * @returns {Object} Map of product IDs to final amounts
   */
  applyOverridesToProducts = (overridesByProduct, validProducts, fullProductsMap, manualOverrideTracking) => {
    const productTotalFeeMap = validProducts.reduce((map, product) => {
      map[product.id] = parseFloat(product.amount)
      return map
    }, {})

    Object.entries(overridesByProduct).forEach(([baseProductId, overrides]) => {
      const selectedProduct = validProducts.find((product) => product.id === baseProductId)
      if (!selectedProduct) {
        return
      }

      // Handle manual fee override
      if (selectedProduct.manual_fee_override_exists) {
        productTotalFeeMap[baseProductId] = parseFloat(selectedProduct.amount)
        this.updateProductOverrideSourceTracking(baseProductId, manualOverrideTracking)
        return
      }

      // Sort and select the highest priority override
      const sortedOverrides = this.sortOverridesByPriority(overrides)
      const selectedOverride = sortedOverrides[0]
      const fullProduct = fullProductsMap.get(baseProductId)

      // Calculate final amount
      const finalAmount = this.calculateFinalProductAmount(selectedOverride, selectedProduct, fullProduct)
      productTotalFeeMap[baseProductId] = finalAmount
    })

    return productTotalFeeMap
  }

  /**
   * Calculate product amounts using appraiser overrides, following handleOverrides logic
   * @param {Object} overrideData - Override data from backend
   * @param {Array} selectedProducts - Currently selected products
   * @param {Array} fullProductsList - Full list of available products
   * @param {Array} additionalFees - Current additional fees array (optional)
   * @param {number} currentKeyCounter - Current key counter value (optional)
   * @returns {Object} { totalFee, updatedProducts, additional_fees, hasAdditionalFees, keyCounter }
   */
  calculateProductAmountsWithOverrides = (
    overrideData,
    selectedProducts,
    fullProductsList,
    additionalFees = [],
    currentKeyCounter = 0,
    useDefaultRushFee = false,
    defaultRushFee = null,
  ) => {
    const { priority } = this.state

    // Create maps for faster lookups
    const validProducts = selectedProducts.filter(Boolean)
    const fullProductsMap = new Map(fullProductsList.map((product) => [product.id, product]))

    // Helper function for manual override tracking
    const manualOverrideTracking = {
      manual_fee_override_exists: true,
      integration_fee_applied: false,
      borrower_amount_override: false,
      fee_mgmt_override: false,
      legacy_vendor_geo_override: false,
      appraiser_override: false,
      legacy_geo_override: false,
      disclosed_amount_cap_applied: false,
      base_product_amount: false,
      fee_mgmt_override_sheet_name: '',
    }

    // Group overrides by base product ID
    const overridesByProduct = this.groupOverridesByProduct(overrideData)

    // Apply overrides to get final product amounts
    const productTotalFeeMap = this.applyOverridesToProducts(
      overridesByProduct,
      validProducts,
      fullProductsMap,
      manualOverrideTracking,
    )

    // Calculate total fee
    const totalFee = Object.values(productTotalFeeMap).reduce((sum, amount) => sum + amount, 0)

    // Calculate additional fees
    const {
      additional_fees: updatedAdditionalFees,
      hasAdditionalFees,
      keyCounter,
    } = this.calculateAdditionalFees(
      additionalFees,
      overrideData,
      priority,
      currentKeyCounter,
      selectedProducts,
      useDefaultRushFee,
      defaultRushFee,
    )

    // Update product amounts
    const updatedProducts = selectedProducts.map((product) => {
      if (!product) {
        return product
      }

      const newAmount = productTotalFeeMap[product.id]
      return newAmount !== undefined ? { ...product, amount: newAmount } : product
    })

    return {
      totalFee,
      updatedProducts,
      additional_fees: updatedAdditionalFees,
      hasAdditionalFees,
      keyCounter,
    }
  }

  /**
   * Handle priority changes to add/remove integration rush fees
   * @param {string} newPriority - The new priority value ('Rush' or 'Normal')
   */
  handlePriorityChange = async (newPriority) => {
    const { selected_products, products, additional_fees, keyCounter, productOverrideDetails } = this.state

    // Update priority first, then recalculate
    this.setState({ priority: newPriority }, () => {
      // Get products that have overrides
      const productIds = Object.keys(productOverrideDetails)
      if (productIds.length === 0) {
        return
      }

      // Build override data from productOverrideDetails
      const overrideData = {}
      productIds.forEach((productId) => {
        const details = productOverrideDetails[productId]
        if (details) {
          overrideData[productId] = details
        }
      })

      // Let calculateProductAmountsWithOverrides handle the integration rush fee logic
      const {
        totalFee,
        updatedProducts,
        additional_fees: updatedAdditionalFees,
        hasAdditionalFees,
        keyCounter: updatedKeyCounter,
      } = this.calculateProductAmountsWithOverrides(
        overrideData,
        selected_products,
        products,
        additional_fees,
        keyCounter,
        this.props.use_default_rush_fee,
        this.props.default_rush_fee,
      )

      // Update state with recalculated results
      this.setState({
        additional_fees: updatedAdditionalFees,
        hasAdditionalFees,
        keyCounter: updatedKeyCounter,
        product_amount: totalFee,
        selected_products: updatedProducts,
      })
    })
  }

  /**
   * Update product override source tracking for a given product
   * @param {string} productId - The product ID
   * @param {Object} newSourceTracking - The new source tracking object
   */
  updateProductOverrideSourceTracking = (productId, newSourceTracking) => {
    this.setState((prevState) => {
      const updatedDetails = { ...prevState.productOverrideDetails }

      // Update or create override details for this product
      if (updatedDetails[productId]) {
        updatedDetails[productId] = {
          ...updatedDetails[productId],
          overrideSourceTracking: newSourceTracking,
        }
      } else {
        updatedDetails[productId] = {
          geoOverrideAmount: null,
          geoOverrideDueDate: null,
          geoOverrideRushDueDate: null,
          integrationRushFee: null,
          overrideSourceTracking: newSourceTracking,
        }
      }

      return {
        productOverrideDetails: updatedDetails,
      }
    })
  }

  /**
   * Add complete override_source_tracking data to products based on their override source tracking
   * Maps API overrideSourceTracking (camelCase) to product override_source_tracking (snake_case)
   * @param {Array} products - Array of product objects
   * @param {Array} selectedAppraisers - Currently selected appraisers
   * @param {Object} productOverrideDetails - Override details from state
   * @returns {Array} Products with override_source_tracking added
   */
  addProductAmountSourceToProducts = (products, selectedAppraisers = [], productOverrideDetails = {}) => {
    return products.map((product) => {
      if (!product) {
        return product
      }

      // Get override source tracking for this product
      let overrideSourceTracking = null

      // If product has manual fee override, use manual override source tracking
      if (product.manual_fee_override_exists) {
        overrideSourceTracking = {
          manual_fee_override_exists: true,
          integration_fee_applied: false,
          borrower_amount_override: false,
          fee_mgmt_override: false,
          legacy_vendor_geo_override: false,
          appraiser_override: false,
          legacy_geo_override: false,
          disclosed_amount_cap_applied: false,
          base_product_amount: false,
          fee_mgmt_override_sheet_name: '',
          integration_fee_error_msg: '',
        }
      } else {
        // If there are selected appraisers, use the first appraiser's override source tracking
        if (selectedAppraisers && selectedAppraisers.length > 0) {
          const firstAppraiserId = selectedAppraisers[0].id
          const appraiserSpecificKey = `${product.id}_${firstAppraiserId}`
          overrideSourceTracking = productOverrideDetails[appraiserSpecificKey]?.overrideSourceTracking
        }

        // Fall back to general product override source tracking
        if (!overrideSourceTracking) {
          overrideSourceTracking = productOverrideDetails[product.id]?.overrideSourceTracking
        }
      }

      // If no override source tracking found, create a default one for base product amount
      if (!overrideSourceTracking) {
        overrideSourceTracking = {
          manual_fee_override_exists: false,
          integration_fee_applied: false,
          borrower_amount_override: false,
          fee_mgmt_override: false,
          legacy_vendor_geo_override: false,
          appraiser_override: false,
          legacy_geo_override: false,
          disclosed_amount_cap_applied: false,
          base_product_amount: true, // Default to base product amount
          fee_mgmt_override_sheet_name: '',
          integration_fee_error_msg: '',
        }
      }

      return {
        ...product,
        override_source_tracking: overrideSourceTracking, // Save as snake_case for product attribute
      }
    })
  }

  /**
   * Update product amounts based on the first selected appraiser's overrides
   */
  updateProductAmountsForFirstAppraiser = async () => {
    const { selectedAppraisers, selected_products, products, productOverrideDetails } = this.state

    if (selectedAppraisers.length === 0) {
      // No appraisers selected, reset to base product amounts
      this.resetToBaseProductAmounts()
      return
    }

    const firstAppraiser = selectedAppraisers[0]

    // Build override data from productOverrideDetails to get full override objects with integrationRushFee
    const overrideData = {}
    const selectedProductIds = selected_products.filter((p) => p).map((p) => p.id)

    // Get appraiser-specific overrides (with suffix)
    Object.keys(productOverrideDetails).forEach((key) => {
      if (key.includes(`_${firstAppraiser.id}`)) {
        const baseProductId = key.split('_')[0]
        if (selectedProductIds.includes(baseProductId)) {
          overrideData[key] = productOverrideDetails[key]
        }
      }
    })

    // Get base product overrides for products without appraiser-specific overrides
    selectedProductIds.forEach((productId) => {
      const hasAppraiserOverride = Object.keys(overrideData).some((key) => key.startsWith(productId + '_'))
      if (!hasAppraiserOverride && productOverrideDetails[productId]) {
        overrideData[productId] = productOverrideDetails[productId]
      }
    })

    if (Object.keys(overrideData).length === 0) {
      // No overrides for first appraiser, but we have appraisers selected, so just keep current amounts
      this.clearProductOverrideLoading()
      return
    }

    const { additional_fees = [], keyCounter = 0 } = this.state
    const {
      totalFee,
      updatedProducts,
      additional_fees: updatedAdditionalFees,
      hasAdditionalFees,
      keyCounter: updatedKeyCounter,
    } = this.calculateProductAmountsWithOverrides(
      overrideData,
      selected_products,
      products,
      additional_fees,
      keyCounter,
      this.props.use_default_rush_fee,
      this.props.default_rush_fee,
    )

    this.setState(
      {
        additional_fees: updatedAdditionalFees,
        hasAdditionalFees,
        keyCounter: updatedKeyCounter,
      },
      () => {
        this.updateProductAmountsAndClearLoading(totalFee, updatedProducts)
      },
    )
  }

  /**
   * Reset product amounts to base values (without appraiser-specific overrides)
   */
  resetToBaseProductAmounts = async () => {
    const { selected_products, products } = this.state
    const product_ids = selected_products
      .filter((product) => product && !product.manual_fee_override_exists)
      .map((product) => product.id)

    if (!product_ids.length) {
      return
    }

    const state = this.getPropertyState()
    const zip = this.getPropertyZip()

    // Get base overrides (without panelist_id)
    this.getProductOverrides(
      product_ids,
      () => this.getPricesFromProducts(product_ids, state, zip, false),
      false,
      null,
    ).then((overrides) => {
      const { additional_fees = [], keyCounter = 0 } = this.state
      const {
        totalFee,
        updatedProducts,
        additional_fees: updatedAdditionalFees,
        hasAdditionalFees,
        keyCounter: updatedKeyCounter,
      } = this.calculateProductAmountsWithOverrides(
        overrides,
        selected_products,
        products,
        additional_fees,
        keyCounter,
        this.props.use_default_rush_fee,
        this.props.default_rush_fee,
      )

      this.setState(
        {
          additional_fees: updatedAdditionalFees,
          hasAdditionalFees,
          keyCounter: updatedKeyCounter,
        },
        () => {
          this.updateProductAmountsAndClearLoading(totalFee, updatedProducts)
        },
      )
    })
  }

  /**
   * Refresh appraiser overrides for all selected appraisers
   * Used when products change to ensure override data is current
   */
  refreshAllAppraiserOverrides = async () => {
    const { selectedAppraisers, panelistFeeMgmtOverrides } = this.state
    const updatedOverrides = { ...panelistFeeMgmtOverrides }

    // Show loading spinner for all product amounts
    this.setProductOverrideLoading()

    // Fetch fresh overrides for all selected appraisers
    const overridePromises = selectedAppraisers.map(async (appraiser) => {
      const overrides = await this.fetchAppraiserOverrides(appraiser.id)
      return { appraiserId: appraiser.id, overrides }
    })

    const overrideResults = await Promise.all(overridePromises)
    overrideResults.forEach(({ appraiserId, overrides }) => {
      updatedOverrides[appraiserId] = overrides
    })

    this.setState(
      {
        panelistFeeMgmtOverrides: updatedOverrides,
      },
      () => {
        // Update product amounts based on first appraiser's fresh overrides
        this.updateProductAmountsForFirstAppraiser()
      },
    )
  }

  /**
   * Set loading state for all product overrides
   */
  setProductOverrideLoading = () => {
    this.setState({ loadingProductOverrides: true })
  }

  /**
   * Clear loading state for all product overrides
   */
  clearProductOverrideLoading = () => {
    this.setState({ loadingProductOverrides: false })
  }

  /**
   * Update product amounts and clear loading state for all products
   * @param {number} totalFee - The calculated total fee
   * @param {Array} updatedProducts - The updated products array
   */
  updateProductAmountsAndClearLoading = (totalFee, updatedProducts) => {
    this.setState({
      product_amount: totalFee,
      selected_products: updatedProducts,
      loadingProductOverrides: false, // Clear loading state
      loadingInitialTotalFee: false, // Also clear initial totalFee loading state
    })
  }

  getInitialAdditionalFee = () => {
    return { key: 0, description: null, amount: null }
  }

  onAdditionalFeeAdd = () => {
    let { additional_fees: additionalFees, keyCounter } = this.state
    if (additionalFees.length === 1 && additionalFees[0] === this.getInitialAdditionalFee()) {
      return
    }
    keyCounter++
    additionalFees.push({
      ...this.getInitialAdditionalFee(),
      key: keyCounter,
    })
    this.setState({
      additional_fees: additionalFees,
      keyCounter,
    })
  }

  shouldShowOverrideSourceTracking = () => {
    const {
      flags: { useOptimizedIntegrationProducts, useNewOverrideLookupEngine },
    } = this.props
    return useOptimizedIntegrationProducts || useNewOverrideLookupEngine
  }

  notShouldShowOverrideSourceTracking = () => {
    const {
      flags: { useOptimizedIntegrationProducts, useNewOverrideLookupEngine },
    } = this.props
    return !useOptimizedIntegrationProducts && !useNewOverrideLookupEngine
  }

  /**
   * Clean additional fees for backend submission
   * Filters fees with description and amount, removes internal tracking attributes
   *
   * @param {Array} additional_fees - Array of additional fees from state
   * @returns {Array} - Cleansed additional fees ready for backend submission
   */
  cleanAdditionalFeesForBackend = (additional_fees) => {
    return additional_fees
      .filter((fee) => fee.description && fee.amount)
      .map((fee) => {
        // Remove internal tracking attributes before sending to backend
        const { isIntegrationRushFee, isProductRushFee, isDefaultRushFee, ...cleansedFee } = fee
        return cleansedFee
      })
  }
}

export default BaseOrderModalContainer
